import axios, { AxiosInstance, AxiosHeaders, InternalAxiosRequestConfig } from 'axios';
import { config } from "./config";

// Create an instance of Axios
const axiosInstance: AxiosInstance = axios.create();

// Variable to track whether a token refresh request is in progress
let isRefreshing = false;
// Array to store pending requests waiting for token refresh
let refreshSubscribers: ((token: string) => void)[] = [];

// Ensure token is initialized at startup if possible
const initializeToken = async () => {
  const token = sessionStorage.getItem('bearerToken');
  if (!token) {
    try {
      const response = await axios.post(`${config.tokenUrl}`);
      const newToken = response.data.token;
      if (newToken) {
        sessionStorage.setItem('bearerToken', newToken);
        console.log('Token initialized successfully');
      }
    } catch (error) {
      console.error('Failed to initialize token:', error);
    }
  }
};

// Try to initialize token immediately
initializeToken();

// Add an interceptor to modify the headers of outgoing requests
axiosInstance.interceptors.request.use(
  async (aIConfig: InternalAxiosRequestConfig) => {
    let token = sessionStorage.getItem('bearerToken');
    if (!token) {
      // If no token is available, get one synchronously
      await initializeToken();
      token = sessionStorage.getItem('bearerToken');
    }
    if (token) {
      if (!aIConfig.headers) {
        aIConfig.headers = new AxiosHeaders();
      }
      aIConfig.headers.set('Authorization', `Bearer ${token}`);
    }
    return aIConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Function to process the queue of waiting requests after token refresh
const processQueue = (token: string) => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

// Add an interceptor to handle token refresh
axiosInstance.interceptors.response.use(
  (response) => response, // onFulfilled

  (error) => { // onRejected
    const originalRequest = error.response?.config ?? error.config;
    
    // Check if this is an unauthorized error (token expired)
    if (error.response && 
        (error.response.status === 401 || 
         (error.response.data && error.response.data[0] === "Authorisation Failed"))) {
         
      // If a refresh is already in progress, queue this request
      if (isRefreshing) {
        return new Promise((resolve) => {
          // Add request to the queue to be retried after token refresh
          refreshSubscribers.push((token: string) => {
            console.log('Retrying queued request with new token');
            
            // Update request headers with new token
            if (originalRequest.headers) {
              if (typeof originalRequest.headers.set === 'function') {
                originalRequest.headers.set('Authorization', `Bearer ${token}`);
              } else {
                originalRequest.headers.Authorization = `Bearer ${token}`;
              }
            }
            
            // Retry the original request with new token
            resolve(axios(originalRequest));
          });
        });
      }
      
      // Start the refresh process if not already in progress
      isRefreshing = true;
      console.log('Starting token refresh process');
      
      // Request new token
      return axios.post(`${config.tokenUrl}`)
        .then((refreshResponse) => {
          const newToken = refreshResponse.data.token;
          console.log('Token refreshed successfully');
          
          // Store new token
          sessionStorage.setItem('bearerToken', newToken);
          
          // Update the current request with new token
          if (originalRequest.headers) {
            if (typeof originalRequest.headers.set === 'function') {
              originalRequest.headers.set('Authorization', `Bearer ${newToken}`);
            } else {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }
          }
          
          // Process all queued requests
          processQueue(newToken);
          
          // Retry original request with new token
          return axios(originalRequest);
        })
        .catch((refreshError) => {
          console.error('Token refresh failed:', refreshError);
          return Promise.reject(refreshError);
        })
        .finally(() => {
          isRefreshing = false;
        });
    }
    
    // For non-401 errors, reject as usual
    return Promise.reject(error);
  }
);

export default axiosInstance;